import { useContext, useEffect, useState } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

// FLUXO DA ROTA (30/09/2026) — mesmo estado que o app Visita usa (GET
// /route-flow + socket company-X-route-flow, ver RouteFlowService no
// backend). Um estado por grupo de rota do Chat Interno. Guardado fora do
// React pra lista e conversa dividirem uma busca e um relógio só.
const flows = {};
const listeners = new Set();
let offset = 0;
let loaded = false;
let boundSocket = null;
let timer = null;

const notify = () => listeners.forEach((fn) => fn());

export const STAGES = {
  aguardando_tempo: { bg: "#f7c948", dim: "#a88414", fg: "#2a1f00", icon: "⏱", text: "AGUARDANDO TEMPO", tag: "⏱ AGUARDANDO TEMPO" },
  em_percurso: { bg: "#1d6fe0", dim: "#0d3f86", fg: "#fff", icon: "▶", text: "EM PERCURSO", tag: "▶ EM PERCURSO" },
  aguardando_pedir: { bg: "#f08c00", dim: "#9a5a00", fg: "#fff", icon: "✎", text: "AGUARDANDO PEDIR ROTA", tag: "✎ AGUARDANDO PEDIR ROTA" },
  aguardando_rota: { bg: "#e5484d", dim: "#8f1f22", fg: "#fff", icon: "!", text: "MOTOBOY AGUARDANDO ROTA", tag: "⚠ AGUARDANDO ROTA" }
};

export const isFlowActive = (st) => Boolean(st && st.enabled && st.shiftActive && STAGES[st.stage]);

export const fmtDuration = (ms) => {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return "—";
  const t = Math.floor(ms / 1000);
  const h = Math.floor(t / 3600);
  const m = String(Math.floor((t % 3600) / 60)).padStart(2, "0");
  const s = String(t % 60).padStart(2, "0");
  return h ? `${h}:${m}:${s}` : `${m}:${s}`;
};

export const flowNow = () => Date.now() + offset;

const applyState = (st) => {
  if (!st || !st.internalChatGroupId) return;
  if (st.serverNow) offset = new Date(st.serverNow).getTime() - Date.now();
  const key = String(st.internalChatGroupId);
  const prev = flows[key];
  flows[key] = { ...st, ticketIds: st.ticketIds || (prev && prev.ticketIds) || [] };
};

// O CRM abre a conversa sem dataWebhook: acha o grupo pelo id do ticket.
const groupIdForTicket = (ticket) => {
  if (!ticket || ticket.channel !== "internal") return null;
  const direct = ticket.dataWebhook && ticket.dataWebhook.internalGroupId;
  if (direct) return String(direct);
  const found = Object.values(flows).find((st) => (st.ticketIds || []).includes(Number(ticket.id)));
  return found ? String(found.internalChatGroupId) : null;
};

const ensureTimer = () => {
  const any = Object.values(flows).some(isFlowActive);
  if (any && !timer) timer = setInterval(notify, 1000);
  if (!any && timer) {
    clearInterval(timer);
    timer = null;
  }
};

const load = async () => {
  try {
    const { data } = await api.get("/route-flow");
    (data || []).forEach(applyState);
    loaded = true;
    ensureTimer();
    notify();
  } catch (err) {
    // sem fluxo configurado / sem rede: só não mostra nada
  }
};

const bindSocket = (socket, companyId) => {
  if (!socket || !companyId || boundSocket === socket) return;
  boundSocket = socket;
  socket.on(`company-${companyId}-route-flow`, (data) => {
    if (data && data.state) {
      applyState(data.state);
      ensureTimer();
      notify();
    }
  });
  socket.on("connect", load);
  // 20/10/2026: ponto novo da localização ao vivo do motoboy (LiveLocationService).
  socket.on(`company-${companyId}-courier-live`, (p) => {
    if (!p) return;
    const st = Object.values(flows).find((x) => String(x.courierId) === String(p.courierId));
    if (st) st.live = { ...(st.live || {}), sharing: p.sharing !== false, stale: false, at: p.at, lat: p.lat, lng: p.lng };
    livePointListeners.forEach((fn) => fn(p));
    notify();
  });
};

// Quem está com o mapa aberto recebe cada ponto novo na hora.
export const livePointListeners = new Set();

// Aviso de espera (5/10/15 min) — ver sendNudge no RouteFlowService.
export const sendNudge = async (st) => {
  const { data } = await api.post(`/route-flow/${st.courierId}/nudge`, { routeKey: st.routeKey });
  applyState(data);
  ensureTimer();
  notify();
  return data;
};

export const toggleShift = async (st) => {
  const { data } = await api.post(`/route-flow/${st.courierId}/shift`, { active: !st.shiftActive });
  applyState(data);
  ensureTimer();
  notify();
  return data;
};

// "Voltar o timer" (01/10/2026) - só admin (checado também no backend).
// Volta pra etapa anterior CONTINUANDO o cronômetro de onde estava (o
// backend já restaura o "stageSince" original, não reseta pra agora).
export const revertStage = async (st) => {
  const { data } = await api.post(`/route-flow/${st.courierId}/revert`);
  applyState(data);
  ensureTimer();
  notify();
  return data;
};

// Devolve o estado do fluxo do grupo (ou null) e re-renderiza a cada segundo
// enquanto algum cronômetro estiver rodando.
export const useRouteFlow = (ticket) => {
  const { socket, user } = useContext(AuthContext);
  const [, setTick] = useState(0);
  // Só conversa do Chat Interno escuta o relógio de 1s: com todos os motoboys
  // ligados, cada linha de WhatsApp da lista redesenhava a cada segundo o dia
  // todo (revisão 01/10/2026). Ticket ainda sem canal (carregando) escuta.
  const listens = !ticket || !ticket.channel || ticket.channel === "internal";
  useEffect(() => {
    if (!loaded) load();
    if (!listens) return undefined;
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, [listens]);
  useEffect(() => {
    bindSocket(socket, user?.companyId);
  }, [socket, user?.companyId]);
  const groupId = groupIdForTicket(ticket);
  return groupId ? flows[groupId] || null : null;
};
